import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ENDPOINTS } from '@shared/constants';
import {
  CommonResponseDTO,
  IReferenceCategoriesPublicResponse,
  IReferenceCategoryPublicResponse,
  IReferenceCategoryReferencesPublicResponse,
  IReferenceResponse,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

@Injectable({ providedIn: 'root' })
export class ReferenceService {
  constructor(private http: HttpClient) {}

  async getBlogReferences(
    categoryId: string
  ): Promise<IReferenceCategoryReferencesPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_BLOG_REFERENCES,
        params: { id: categoryId },
      });
      firstValueFrom(
        this.http.get<
          CommonResponseDTO<IReferenceCategoryReferencesPublicResponse[]>
        >(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getNewsReferences(): // categoryId: string
  Promise<IReferenceCategoryReferencesPublicResponse[]> {
    return null;
  }

  async getCaseStudyReferences(): // categoryId: string
  Promise<IReferenceResponse[]> {
    // Implement
    return null;
  }

  async getActivityReferences(
    activityCategoryId: string
  ): Promise<IReferenceCategoryReferencesPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_ACTIVITY_REFERENCES,
        params: { id: activityCategoryId },
      });
      firstValueFrom(
        this.http.get<
          CommonResponseDTO<IReferenceCategoryReferencesPublicResponse[]>
        >(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getUniversityReferences(
    activityCategoryId: string
  ): Promise<IReferenceCategoryReferencesPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint:
          ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_UNIVERSITY_REFERENCES,
        params: { id: activityCategoryId },
      });
      firstValueFrom(
        this.http.get<
          CommonResponseDTO<IReferenceCategoryReferencesPublicResponse[]>
        >(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getSubmissionReferences(
    submissionsCategoryId: string
  ): Promise<IReferenceCategoryReferencesPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint:
          ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_SUBMISSION_REFERENCES,
        params: { id: submissionsCategoryId },
      });
      firstValueFrom(
        this.http.get<
          CommonResponseDTO<IReferenceCategoryReferencesPublicResponse[]>
        >(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getCategoryByName(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getBlogCategoryByName(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_BLOG__CATEGORIES_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getCaseStudiesCategoryByName(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint:
          ENDPOINTS.REFERENCE_CASE_STUDIES__CATEGORIES_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getNewsCategoryByName(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_NEWS__CATEGORIES_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getVacancyReference(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_VACANCIES_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getGeneralReference(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_GENERAL_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getSubmissionCategoryByName(
    categoryName: string
  ): Promise<IReferenceCategoryPublicResponse> {
    return new Promise((resolve, reject) => {
      const url = `${generateURL({
        endpoint: ENDPOINTS.REFERENCE_SUBMISSIONS_PUBLIC_GET_SINGLE,
      })}?category_name=${categoryName}`;
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse>>(url)
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getVacancyCategoryById(
    id: string
  ): Promise<IReferenceCategoryPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_CATEGORY,
        params: { id },
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getVacancyLocationById(
    id: string
  ): Promise<IReferenceCategoryPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_LOCATION,
        params: { id },
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getVacancyModalityById(
    id: string
  ): Promise<IReferenceCategoryPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_MODALITY,
        params: { id },
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getVacancyTypesById(
    id: string
  ): Promise<IReferenceCategoryPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint: ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_TYPES,
        params: { id },
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoryPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getVacancyCategories(): Promise<IReferenceCategoriesPublicResponse[]> {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint:
          ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_ALL_VACANCY_CATEGORIES,
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoriesPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async getQuestionAnswers(): Promise<
    IReferenceCategoriesPublicResponse[] | undefined
  > {
    return new Promise((resolve, reject) => {
      const url = generateURL({
        endpoint:
          ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_GET_TO_KNOW_QUESTIONS_ANSWERS,
      });
      firstValueFrom(
        this.http.get<CommonResponseDTO<IReferenceCategoriesPublicResponse[]>>(
          url
        )
      )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
